import { ReactNode } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import FilterCollapseHeader from "components/filter_form/filter_collapse_header/FilterCollapseHeader";
import Button from "components/ui/button/Button";
import Checkbox from "components/ui/checkbox/Checkbox";
import CollapseGroup, { CollapseItem } from "components/ui/collapse_group/CollapseGroup";
import Input from "components/ui/input/Input";
import Badge from "lib/core_components/Badge";
import {
  CognismContactFilters,
  CONTACT_FILTER_GROUP_COUNTS,
  CONTACT_FILTER_GROUP_RESETS,
  ContactFilterGroupKey,
  countActiveFilters,
  EMPTY_CONTACT_FILTERS,
  phoneParentChecked,
  togglePhoneParent,
} from "./cognismContactFilters";
import CognismContactsBuyersFilter from "./CognismContactsBuyersFilter";
import JobFunctionFilter from "./JobFunctionFilter";
import JobTitleFilter from "./JobTitleFilter";
import SeniorityTierFilter from "./SeniorityTierFilter";

export type CognismContactsFilterSectionsProps = {
  filters: CognismContactFilters;
  onChange: (filters: CognismContactFilters) => void;
  /** Sections to omit (embedded surfaces lock the buyer scope and search inline). */
  hiddenSections?: ContactFilterGroupKey[];
};

/**
 * The accordion filter sections shared by the sidebar (standalone /contacts) and the
 * filter drawer (embedded surfaces). The host shell owns the header around them.
 */
export function CognismContactsFilterSections({
  filters,
  onChange,
  hiddenSections = [],
}: CognismContactsFilterSectionsProps) {
  const allGroups: { key: ContactFilterGroupKey; title: string; content: ReactNode }[] = [
    {
      key: "role",
      title: "Role",
      content: (
        <SectionBody>
          <Field>
            <Text fontWeight="600">Job title</Text>
            <JobTitleFilter
              value={filters.jobTitles}
              onChange={(jobTitles) => onChange({ ...filters, jobTitles })}
            />
          </Field>
          <Field>
            <SeniorityTierFilter
              value={filters.seniorityTiers}
              onChange={(seniorityTiers) => onChange({ ...filters, seniorityTiers })}
            />
          </Field>
          <Field>
            <JobFunctionFilter
              value={filters.jobFunctions}
              onChange={(jobFunctions) => onChange({ ...filters, jobFunctions })}
            />
          </Field>
        </SectionBody>
      ),
    },
    {
      key: "contactInformation",
      title: "Contact information",
      content: (
        <SectionBody>
          <Field>
            {/* Phone types are nested under a tri-state parent and OR together, so both
                ticked means "any phone" (mirrors the CPV / buyer-type trees). */}
            <ToggleRow
              label="Has phone number"
              checked={phoneParentChecked(filters)}
              onCheckedChange={() => onChange(togglePhoneParent(filters))}
            />
            <NestedRows>
              <ToggleRow
                label="Mobile phone"
                checked={filters.hasMobilePhone}
                onCheckedChange={(hasMobilePhone) => onChange({ ...filters, hasMobilePhone })}
              />
              <ToggleRow
                label="Direct phone"
                checked={filters.hasDirectPhone}
                onCheckedChange={(hasDirectPhone) => onChange({ ...filters, hasDirectPhone })}
              />
            </NestedRows>
          </Field>
          <ToggleRow
            label="Has email"
            checked={filters.hasEmail}
            onCheckedChange={(hasEmail) => onChange({ ...filters, hasEmail })}
          />
        </SectionBody>
      ),
    },
    {
      key: "buyers",
      title: "Buyers",
      content: (
        <SectionBody>
          <CognismContactsBuyersFilter
            value={{
              buyerGuids: filters.buyerGuids,
              buyerListIds: filters.buyerListIds,
              buyerCategoryIds: filters.buyerCategoryIds,
            }}
            onChange={(buyer) => onChange({ ...filters, ...buyer })}
          />
        </SectionBody>
      ),
    },
    {
      key: "contactName",
      title: "Contact name",
      content: (
        <SectionBody>
          <Input
            name="contactSearchQuery"
            aria-label="Search by name"
            placeholder="Search by name"
            value={filters.searchQuery}
            onChange={(e) => onChange({ ...filters, searchQuery: e.target.value })}
          />
        </SectionBody>
      ),
    },
  ];

  const groups = allGroups.filter((group) => !hiddenSections.includes(group.key));

  const items: CollapseItem[] = groups.map((group) => ({
    key: group.key,
    header: (
      <FilterCollapseHeader
        title={group.title}
        count={CONTACT_FILTER_GROUP_COUNTS[group.key](filters)}
        clear={() => onChange({ ...filters, ...CONTACT_FILTER_GROUP_RESETS[group.key] })}
      />
    ),
    content: group.content,
  }));

  return (
    // Rendered full-width (no wrapper padding) so section dividers reach the host's
    // edges like Notices; the 24px inset lives on the trigger + section content.
    <CollapseGroup
      items={items}
      defaultValue={groups.map((group) => group.key)}
      triggerStyles={{ padding: "16px 24px" }}
    />
  );
}

export type CognismContactsFilterPanelProps = {
  filters: CognismContactFilters;
  onChange: (filters: CognismContactFilters) => void;
  /** Collapse the sidebar (the funnel button in the results toolbar reopens it). */
  onCollapse: () => void;
};

/**
 * Inline filter sidebar for the contacts table (mirrors the Notices 2.0 layout).
 * Filters are grouped into collapsible accordion sections — role, contact information,
 * buyers, and contact name. Changes apply live (the parent owns the filter state).
 */
function CognismContactsFilterPanel({
  filters,
  onChange,
  onCollapse,
}: CognismContactsFilterPanelProps) {
  const activeCount = countActiveFilters(filters);

  return (
    <Flex column aria-label="Contacts filters">
      {/* Local header (not the shared FilterFormTitle) so the collapse control is always
          available — FilterFormTitle only renders it when there are active filters. */}
      <SidebarHeader>
        <Flex alignItems="center" gap={8}>
          <HeaderTitle>Filters</HeaderTitle>
          <Badge size={18}>{activeCount}</Badge>
          {activeCount > 0 && (
            <Button buttonType="tertiary" onClick={() => onChange({ ...EMPTY_CONTACT_FILTERS })}>
              Clear all
            </Button>
          )}
        </Flex>
        <Button
          buttonType="secondary"
          icon="chevronLeftDouble"
          aria-label="Close filters"
          onClick={onCollapse}
        />
      </SidebarHeader>
      <CognismContactsFilterSections filters={filters} onChange={onChange} />
    </Flex>
  );
}

function ToggleRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  /** `"indeterminate"` renders the tri-state dash, for a partially-selected parent. */
  checked: boolean | "indeterminate";
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <ToggleLabel>
      <Checkbox
        aria-label={label}
        checked={checked}
        onCheckedChange={(state) => onCheckedChange(state === true)}
      />
      <Text>{label}</Text>
    </ToggleLabel>
  );
}

// Sidebar header — mirrors FilterFormTitle's sidebar look, but keeps the collapse
// button always visible so the sidebar can be collapsed even with no active filters.
const SidebarHeader = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  height: 64,
}));

const HeaderTitle = styled.div(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "125%",
}));

// Content wrapper inside each accordion section — 24px horizontal inset (dividers stay
// full-width) plus top/bottom breathing room and a wider gap between distinct fields
// (label+control pairs are grouped tighter via Field).
const SectionBody = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  padding: "8px 24px 24px",
});

// A single label+control pair, kept tightly spaced within a section.
const Field = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const ToggleLabel = styled.label({
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
});

// Child rows of a nested filter group, indented to sit under the parent's label
// (checkbox width + its label gap).
const NestedRows = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  paddingLeft: 24,
});

export default CognismContactsFilterPanel;
